/**
 * The review-cohort trio, read once through ruling 32's seam.
 *
 * Four of the five session-header modal cores branch on the same
 * three app values, and app-side each of them resolved the same three legs:
 * `useRBACPermissionCheck().isReviewingOtherUser` ·
 * `useCombinedQueriesTeamGeneralInfo().teamDetails?.team.id` ·
 * `useAtomValue(selectedReviewMemberAtom)`. A headless package may read none
 * of them, so they arrive through the ONE required
 * `SessionDataConfig.useSessionAccessContext` member that ruling 32 introduced
 * (`docs/monorepo-ui-floor-rulings/32-the-session-access-context-seam.md`),
 * resolved app-side in `src/hooks/useSessionAccessContext.ts`. This module is
 * the single site that calls it for the cohort, so the three decisions below
 * are made once instead of four times.
 *
 * ### Why the argument is `''`
 *
 * The member's signature is `useSessionAccessContext(sessionId)`, and none of
 * these four hooks is scoped to a session AT RENDER — three are mutations whose
 * session id arrives in the mutation input, and the fourth is keyed by a
 * patient profile. The argument is safe to pass empty because it is
 * argument-independent for everything read here: `sessionId` reaches only
 * `useSessionPermissionCheck(sessionId)`, whose single output is
 * `hasSharedSessionEditPermission` — a member this module deliberately does not
 * expose, precisely so no core can come to depend on a permission resolved
 * against the empty id. `sessionReviewCohort.test.ts` pins that.
 *
 * ### Why this costs no new request
 *
 * `useSessionAccessContext` subscribes more widely than the trio did:
 * `useRouter`, `useIsAuthenticated`, `useQueryUser` and
 * `useSessionPermissionCheck` on top of `useCombinedQueriesTeamGeneralInfo`.
 * None of the additions fetches at these call sites. `useQueryUser` is already
 * mounted by the cohort read itself (`useIsReviewingOtherUser` reads it) and by
 * `useCombinedQueriesTeamGeneralInfo`; `useSessionPermissionCheck`'s
 * `useQueryJoinedTeams` is already mounted INSIDE
 * `useCombinedQueriesTeamGeneralInfo`, which every one of these four hooks
 * already called; the permissions query itself is `enabled: !!sessionId`, so
 * the empty id keeps it disabled; and the router, the auth verdict and
 * `useIsOfflineConnectivity` perform no I/O. The fifth core,
 * `useQuerySupportedLanguages`, does NOT read this module — see its own header
 * for why that one leaf keeps a delegate-resolved parameter instead.
 *
 * ### The two derivations are not interchangeable
 *
 * `isReviewingOtherUser` here is the context's COHORT read
 * (`isReviewingOtherUserCohort`), which is `useIsReviewingOtherUser()` inlined
 * — the exact expression `useRBACPermissionCheck` returned to all four hooks.
 * The context's other member of that name is the session-by-id BRANCH read,
 * which additionally requires a loaded team id. Ruling 32 records why
 * collapsing them is wrong and unreported; here the wrong one would send a
 * reviewing user's write down the personal endpoint while team details are
 * still resolving.
 */
import { getSessionDataConfig } from './config';

export interface SessionReviewCohort {
  /**
   * The COHORT read — `useRBACPermissionCheck().isReviewingOtherUser`, which is
   * `useIsReviewingOtherUser()`. NOT the session-by-id branch read.
   */
  isReviewingOtherUser: boolean;
  /**
   * `teamDetails?.team.id`, restored to `undefined` when unresolved.
   *
   * The context carries `teamId: string` (`?? ''`) because its own consumer
   * only ever tests it for truthiness. Two readers here put the value straight
   * into a cache key, so the empty string would be a THIRD spelling of "not
   * resolved yet" that no writer or invalidator addresses. `''` is mapped back.
   */
  teamId: string | undefined;
  /** `selectedReviewMemberAtom`, restored to `undefined` when unset. */
  memberId: string | undefined;
}

export function useSessionReviewCohort(): SessionReviewCohort {
  const { isReviewingOtherUserCohort, teamId, memberId } =
    getSessionDataConfig().useSessionAccessContext('');

  return {
    isReviewingOtherUser: isReviewingOtherUserCohort,
    teamId: teamId || undefined,
    memberId: memberId || undefined,
  };
}
